import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import {
  Search, X, Download, Trash2, Check, Loader2,
  Shield, Monitor, MessageSquare, BarChart3,
  CreditCard, Users, Code2, ClipboardCheck, Puzzle,
  Star, AlertCircle,
} from 'lucide-react';

const ACCENT = '#f97316';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ConfigField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'url' | 'select' | 'toggle';
  required: boolean;
  placeholder?: string;
  options?: string[];
}

interface Integration {
  id: string;
  name: string;
  description: string;
  long_description: string;
  category: string;
  vendor: string;
  icon_url: string;
  install_count: number;
  rating: number;
  status: 'available' | 'installed' | 'coming_soon';
  config_schema: ConfigField[];
  features: string[];
  installed_at?: string | null;
  installed_by_email?: string | null;
  last_tested_at?: string | null;
  last_test_status?: 'success' | 'error' | null;
  last_test_message?: string | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { id: 'all', label: 'All', Icon: Puzzle },
  { id: 'productivity', label: 'Productivity', Icon: BarChart3 },
  { id: 'security', label: 'Security', Icon: Shield },
  { id: 'communication', label: 'Communication', Icon: MessageSquare },
  { id: 'monitoring', label: 'Monitoring', Icon: Monitor },
  { id: 'billing', label: 'Billing', Icon: CreditCard },
  { id: 'crm', label: 'CRM / PSA', Icon: Users },
  { id: 'development', label: 'Development', Icon: Code2 },
  { id: 'compliance', label: 'Compliance', Icon: ClipboardCheck },
] as const;

const CATEGORY_COLORS: Record<string, { bg: string; color: string }> = {
  productivity: { bg: '#dbeafe', color: '#1e40af' },
  security: { bg: '#fee2e2', color: '#991b1b' },
  communication: { bg: '#ede9fe', color: '#5b21b6' },
  monitoring: { bg: '#fef3c7', color: '#92400e' },
  billing: { bg: '#dcfce7', color: '#166534' },
  crm: { bg: '#cffafe', color: '#0e7490' },
  development: { bg: '#fed7aa', color: '#9a3412' },
  compliance: { bg: '#ccfbf1', color: '#0f766e' },
};

type InstallStep = 'idle' | 'testing' | 'tested_ok' | 'tested_fail' | 'saving';

// ─── Install Modal ────────────────────────────────────────────────────────────

function InstallModal({
  integration,
  onClose,
  onSuccess,
}: {
  integration: Integration;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [config, setConfig] = useState<Record<string, string>>({});
  const [step, setStep] = useState<InstallStep>('idle');
  const [testMessage, setTestMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const isInstalled = integration.status === 'installed';
  const isComingSoon = integration.status === 'coming_soon';

  const handleInstall = async () => {
    // Validate required fields
    for (const field of integration.config_schema) {
      if (field.required && !config[field.key]) {
        setErrorMessage(`${field.label} is required`);
        return;
      }
    }
    setErrorMessage('');
    setStep('testing');

    try {
      // Step 1: Test connection
      const testResult = await api.post<{ success: boolean; message: string }>(
        `/integrations/${integration.id}/test-connection`,
        { config },
      );

      if (!testResult.success) {
        setStep('tested_fail');
        setTestMessage(testResult.message);
        return;
      }

      setStep('tested_ok');
      setTestMessage(testResult.message);

      // Brief pause so user sees the success state
      await new Promise(r => setTimeout(r, 800));

      // Step 2: Save
      setStep('saving');
      await api.post(`/integrations/${integration.id}/install`, { config });
      onSuccess();
    } catch (err: any) {
      setStep('tested_fail');
      setTestMessage(err.message || 'Failed to connect — check your credentials and try again');
    }
  };

  const buttonLabel = () => {
    switch (step) {
      case 'testing': return 'Testing connection...';
      case 'tested_ok': return 'Connected ✓';
      case 'saving': return 'Saving...';
      default: return 'Install';
    }
  };

  const catColor = CATEGORY_COLORS[integration.category] || { bg: '#f3f4f6', color: '#374151' };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
        paddingTop: '5vh', zIndex: 200, overflow: 'auto',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        backgroundColor: '#fff', borderRadius: '0.75rem', width: '100%', maxWidth: 680,
        maxHeight: '88vh', overflow: 'auto', position: 'relative',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '1.5rem 1.5rem 1.25rem', borderBottom: '1px solid #f3f4f6',
          position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
            <div style={{
              width: 52, height: 52, borderRadius: '0.625rem',
              backgroundColor: '#f9fafb', border: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Puzzle size={24} color="#9ca3af" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: '#111827' }}>
                {integration.name}
              </h2>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.8125rem', color: '#9ca3af' }}>
                by {integration.vendor}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {/* Stats row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <Download size={14} />
              <span>{integration.install_count.toLocaleString()} installs</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#d97706' }}>
              <Star size={14} fill="#d97706" />
              <span>{integration.rating.toFixed(1)}</span>
            </div>
            <span style={{
              padding: '0.125rem 0.5rem', borderRadius: '9999px',
              fontSize: '0.75rem', fontWeight: 500,
              backgroundColor: catColor.bg, color: catColor.color,
            }}>
              {integration.category}
            </span>
          </div>

          {/* Description */}
          <div style={{ marginBottom: '1.25rem' }}>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#4b5563', lineHeight: 1.6 }}>
              {integration.long_description}
            </p>
          </div>

          {/* Features */}
          <div style={{ marginBottom: '1.25rem' }}>
            <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>Features</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.375rem' }}>
              {integration.features.map(f => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8125rem', color: '#6b7280' }}>
                  <Check size={13} color="#10b981" />
                  {f}
                </div>
              ))}
            </div>
          </div>

          {/* Installed info */}
          {isInstalled && integration.installed_at && (
            <div style={{
              marginBottom: '1.25rem', padding: '0.875rem 1rem',
              backgroundColor: '#f0fdf4', borderRadius: '0.5rem',
              border: '1px solid #bbf7d0',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                <Check size={15} color="#16a34a" />
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#16a34a' }}>Installed</span>
              </div>
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>
                Installed {new Date(integration.installed_at).toLocaleDateString()} by {integration.installed_by_email}
              </p>
              {integration.last_test_status && (
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: integration.last_test_status === 'success' ? '#16a34a' : '#dc2626' }}>
                  Last test: {integration.last_test_message}
                </p>
              )}
            </div>
          )}

          {/* Config form — only for available integrations */}
          {!isInstalled && !isComingSoon && integration.config_schema.length > 0 && (
            <div style={{ marginBottom: '1.25rem' }}>
              <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>Configuration</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {integration.config_schema.map(field => (
                  <div key={field.key}>
                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: '#374151', marginBottom: '0.375rem' }}>
                      {field.label}
                      {field.required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
                    </label>
                    {field.type === 'select' ? (
                      <select
                        value={config[field.key] || ''}
                        onChange={e => setConfig(p => ({ ...p, [field.key]: e.target.value }))}
                        style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: '1px solid #d1d5db', fontSize: '0.875rem', outline: 'none' }}
                      >
                        <option value="">Select...</option>
                        {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : field.type === 'toggle' ? (
                      <button
                        onClick={() => setConfig(p => ({ ...p, [field.key]: p[field.key] === 'true' ? 'false' : 'true' }))}
                        style={{
                          position: 'relative', width: 44, height: 24, borderRadius: 12,
                          border: 'none', cursor: 'pointer', transition: 'background 0.2s',
                          backgroundColor: config[field.key] === 'true' ? ACCENT : '#d1d5db',
                        }}
                      >
                        <span style={{
                          position: 'absolute', top: 2, left: config[field.key] === 'true' ? 22 : 2,
                          width: 20, height: 20, borderRadius: '50%', backgroundColor: '#fff',
                          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }} />
                      </button>
                    ) : (
                      <input
                        type={field.type === 'password' ? 'password' : 'text'}
                        value={config[field.key] || ''}
                        onChange={e => setConfig(p => ({ ...p, [field.key]: e.target.value }))}
                        placeholder={field.placeholder}
                        style={{
                          width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                          border: '1px solid #d1d5db', fontSize: '0.875rem',
                          outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Test result message */}
          {step === 'tested_ok' && (
            <div style={{
              marginBottom: '1rem', padding: '0.625rem 0.875rem',
              backgroundColor: '#f0fdf4', borderRadius: '0.375rem',
              border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: '0.5rem',
              fontSize: '0.875rem', color: '#16a34a',
            }}>
              <Check size={15} />
              {testMessage}
            </div>
          )}
          {step === 'tested_fail' && (
            <div style={{
              marginBottom: '1rem', padding: '0.625rem 0.875rem',
              backgroundColor: '#fef2f2', borderRadius: '0.375rem',
              border: '1px solid #fecaca', display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
              fontSize: '0.875rem', color: '#dc2626',
            }}>
              <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{testMessage}</span>
            </div>
          )}

          {/* Validation error */}
          {errorMessage && (
            <div style={{
              marginBottom: '1rem', padding: '0.625rem 0.875rem',
              backgroundColor: '#fef2f2', borderRadius: '0.375rem',
              border: '1px solid #fecaca',
              fontSize: '0.875rem', color: '#dc2626',
            }}>
              {errorMessage}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.625rem', paddingTop: '0.25rem' }}>
            {isComingSoon && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.5rem 1.25rem', borderRadius: '0.375rem',
                backgroundColor: '#f3f4f6', color: '#9ca3af',
                fontSize: '0.875rem', fontWeight: 500,
              }}>
                Coming Soon
              </span>
            )}
            {!isInstalled && !isComingSoon && (
              <button
                onClick={handleInstall}
                disabled={step === 'testing' || step === 'saving' || step === 'tested_ok'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                  padding: '0.5rem 1.25rem', borderRadius: '0.375rem',
                  backgroundColor: step === 'tested_ok' ? '#16a34a' : ACCENT,
                  color: '#fff', border: 'none', cursor: (step === 'testing' || step === 'saving' || step === 'tested_ok') ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem', fontWeight: 600, opacity: (step === 'testing' || step === 'saving') ? 0.7 : 1,
                  transition: 'background 0.2s',
                }}
              >
                {(step === 'testing' || step === 'saving') && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
                {step === 'tested_ok' && <Check size={14} />}
                {step === 'tested_fail' && <Download size={14} />}
                {step === 'idle' && <Download size={14} />}
                {buttonLabel()}
              </button>
            )}
            {step === 'tested_fail' && !isInstalled && (
              <span style={{ fontSize: '0.8125rem', color: '#9ca3af', display: 'flex', alignItems: 'center' }}>
                Fix the error above and try again
              </span>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem 1rem', borderRadius: '0.375rem',
                border: '1px solid #e5e7eb', backgroundColor: '#fff',
                color: '#6b7280', cursor: 'pointer', fontSize: '0.875rem',
              }}
            >
              {isInstalled ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Integration Card ─────────────────────────────────────────────────────────

function IntegrationCard({ integration, onClick }: { integration: Integration; onClick: () => void }) {
  const catColor = CATEGORY_COLORS[integration.category] || { bg: '#f3f4f6', color: '#374151' };
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: '#fff', borderRadius: '0.5rem',
        border: `1px solid ${hovered ? '#d1d5db' : '#e5e7eb'}`,
        padding: '1.25rem', cursor: 'pointer',
        boxShadow: hovered ? '0 4px 12px rgba(0,0,0,0.07)' : 'none',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        display: 'flex', flexDirection: 'column', gap: '0.625rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{
          width: 44, height: 44, borderRadius: '0.5rem',
          backgroundColor: '#f9fafb', border: '1px solid #e5e7eb',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Puzzle size={20} color="#9ca3af" />
        </div>
        {integration.status === 'installed' && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
            padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.6875rem',
            fontWeight: 600, backgroundColor: '#dcfce7', color: '#16a34a',
          }}>
            <Check size={10} />
            Installed
          </span>
        )}
        {integration.status === 'coming_soon' && (
          <span style={{
            padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.6875rem',
            fontWeight: 500, backgroundColor: '#f3f4f6', color: '#9ca3af',
          }}>
            Coming Soon
          </span>
        )}
      </div>

      <div>
        <h3 style={{ margin: '0 0 0.125rem', fontSize: '0.9375rem', fontWeight: 600, color: '#111827' }}>
          {integration.name}
        </h3>
        <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af' }}>by {integration.vendor}</p>
      </div>

      <p style={{
        margin: 0, fontSize: '0.8125rem', color: '#4b5563', lineHeight: 1.5,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
      }}>
        {integration.description}
      </p>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: '0.5rem', borderTop: '1px solid #f3f4f6' }}>
        <span style={{
          padding: '0.125rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.6875rem',
          fontWeight: 500, backgroundColor: catColor.bg, color: catColor.color,
        }}>
          {integration.category}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.75rem', color: '#9ca3af' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Download size={11} />
            {integration.install_count.toLocaleString()}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#d97706' }}>
            <Star size={11} fill="#d97706" />
            {integration.rating.toFixed(1)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function IntegrationsPage() {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [activeTab, setActiveTab] = useState<'browse' | 'installed'>('browse');
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [installedCount, setInstalledCount] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');

  const loadIntegrations = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'installed') {
        const data = await api.get<{ integrations: Integration[]; total: number }>('/integrations/installed');
        setIntegrations(data.integrations);
        setInstalledCount(data.total);
      } else {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (category !== 'all') params.set('category', category);
        const data = await api.get<{ integrations: Integration[]; total: number }>(`/integrations?${params}`);
        setIntegrations(data.integrations);
        // Update installed count from browse data
        setInstalledCount(data.integrations.filter(i => i.status === 'installed').length);
      }
    } catch { /* empty */ }
    setLoading(false);
  }, [activeTab, search, category]);

  useEffect(() => { loadIntegrations(); }, [loadIntegrations]);

  const handleInstallSuccess = () => {
    setSelectedIntegration(null);
    setSuccessMessage('Integration installed successfully');
    setTimeout(() => setSuccessMessage(''), 4000);
    loadIntegrations();
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.375rem', fontWeight: 700, color: '#111827' }}>
          Integration Marketplace
        </h1>
        <p style={{ margin: 0, fontSize: '0.9375rem', color: '#6b7280' }}>
          Connect third-party tools and services to your stack
        </p>
      </div>

      {/* Success toast */}
      {successMessage && (
        <div style={{
          marginBottom: '1rem', padding: '0.75rem 1rem',
          backgroundColor: '#f0fdf4', borderRadius: '0.5rem',
          border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: '0.5rem',
          fontSize: '0.875rem', color: '#16a34a',
        }}>
          <Check size={16} />
          {successMessage}
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.25rem', padding: '0.25rem', backgroundColor: '#f3f4f6', borderRadius: '0.5rem', width: 'fit-content' }}>
        {(['browse', 'installed'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.5rem 1rem', borderRadius: '0.375rem', border: 'none',
              cursor: 'pointer', fontSize: '0.875rem', fontWeight: activeTab === tab ? 600 : 400,
              backgroundColor: activeTab === tab ? '#fff' : 'transparent',
              color: activeTab === tab ? '#111827' : '#6b7280',
              boxShadow: activeTab === tab ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
              display: 'flex', alignItems: 'center', gap: '0.375rem',
            }}
          >
            {tab === 'browse' ? 'Browse' : 'Installed'}
            {tab === 'installed' && installedCount > 0 && (
              <span style={{
                minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10,
                backgroundColor: ACCENT, color: '#fff',
                fontSize: '0.6875rem', fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {installedCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters — browse tab only */}
      {activeTab === 'browse' && (
        <div style={{ marginBottom: '1.25rem' }}>
          {/* Search */}
          <div style={{ position: 'relative', maxWidth: 440, marginBottom: '0.875rem' }}>
            <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: '#9ca3af' }} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search integrations..."
              style={{
                width: '100%', padding: '0.5rem 0.75rem 0.5rem 2.125rem',
                borderRadius: '0.375rem', border: '1px solid #d1d5db',
                fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{ position: 'absolute', right: 8, top: 9, border: 'none', background: 'transparent', cursor: 'pointer', color: '#9ca3af' }}
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Category tabs */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
            {CATEGORIES.map(({ id: catId, label, Icon }) => (
              <button
                key={catId}
                onClick={() => setCategory(catId)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                  padding: '0.375rem 0.75rem', borderRadius: '0.375rem', cursor: 'pointer',
                  fontSize: '0.8125rem', fontWeight: category === catId ? 600 : 400,
                  border: `1px solid ${category === catId ? ACCENT : '#d1d5db'}`,
                  backgroundColor: category === catId ? '#fff7ed' : '#fff',
                  color: category === catId ? ACCENT : '#6b7280',
                }}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ height: 200, borderRadius: '0.5rem', backgroundColor: '#f3f4f6', animation: 'pulse 1.5s ease-in-out infinite' }} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && integrations.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
          <Puzzle size={48} style={{ margin: '0 auto 1rem', opacity: 0.4 }} />
          <p style={{ fontSize: '1rem', fontWeight: 500, color: '#6b7280', margin: '0 0 0.5rem' }}>
            {activeTab === 'installed' ? 'No integrations installed yet' : 'No integrations found'}
          </p>
          <p style={{ fontSize: '0.875rem', margin: 0 }}>
            {activeTab === 'installed' ? 'Browse the marketplace to install integrations' : 'Try adjusting your search or filters'}
          </p>
          {activeTab === 'installed' && (
            <button
              onClick={() => setActiveTab('browse')}
              style={{
                marginTop: '1rem', padding: '0.5rem 1.25rem',
                backgroundColor: ACCENT, color: '#fff',
                border: 'none', borderRadius: '0.375rem',
                fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Browse Marketplace
            </button>
          )}
        </div>
      )}

      {/* Integration grid */}
      {!loading && integrations.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
          {integrations.map(integration => (
            <IntegrationCard
              key={integration.id}
              integration={integration}
              onClick={() => setSelectedIntegration(integration)}
            />
          ))}
        </div>
      )}

      {/* Detail / Install modal */}
      {selectedIntegration && (
        <InstallModal
          integration={selectedIntegration}
          onClose={() => setSelectedIntegration(null)}
          onSuccess={handleInstallSuccess}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
      `}</style>
    </div>
  );
}
